import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addToCart } from '../../store/cartSlice';
import { API_ENDPOINTS } from '../../config/api';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { showError, showSuccess } from '../../utils/sweetAlert';
import Loader from '../shared/Loader';
import SEO from '../shared/SEO';

const PRODUCT_TYPES = {
  cuadro: { label: 'Cuadro', icon: 'fa-image' },
  remera: { label: 'Remera', icon: 'fa-shirt' },
  almohadon: { label: 'Almohadón', icon: 'fa-couch' },
  otro: { label: 'Otro', icon: 'fa-box' },
};

const CustomizableProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    instructions: '',
    selectedSize: '',
    quantity: 1,
  });
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [customImage, setCustomImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.customizableProducts.getById(id));
      if (!res.ok) throw new Error('Producto no encontrado');
      const data = await res.json();
      setProduct(data);
      if (data.sizes && data.sizes.length > 0) {
        setFormData(prev => ({ ...prev, selectedSize: data.sizes[0] }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      showError('Solo se permiten imágenes (JPEG, PNG, WebP, GIF)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showError('La imagen no puede superar los 10MB');
      return;
    }

    setCustomImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const totalPrice = product ? product.basePrice * formData.quantity : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!customImage) return showError('Subí tu foto para personalizar el producto');

    // Validar campos personalizados requeridos
    if (product.customFields?.length > 0) {
      for (const field of product.customFields) {
        if (field.required && !customFieldValues[field.label]?.toString().trim()) {
          return showError(`Completá el campo "${field.label}"`);
        }
      }
    }

    setIsSubmitting(true);
    try {
      // 1. Subir imagen a Wasabi
      const uploadData = new FormData();
      uploadData.append('customImage', customImage);

      const uploadRes = await fetch(API_ENDPOINTS.customizableOrders.uploadImage(), {
        method: 'POST',
        body: uploadData,
      });

      const uploadResult = await uploadRes.json();
      if (!uploadRes.ok) throw new Error(uploadResult.error || 'Error al subir la imagen');

      // 2. Agregar al carrito con datos de personalización
      const cartItem = {
        _id: product._id,
        cartItemId: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        name: product.name,
        price: product.basePrice,
        quantity: formData.quantity,
        images: product.images,
        isCustomizable: true,
        customizableProductId: product._id,
        productType: product.productType,
        selectedSize: formData.selectedSize || null,
        customImage: {
          url: uploadResult.url,
          key: uploadResult.key,
          originalName: uploadResult.originalName
        },
        customFieldValues: { ...customFieldValues },
        instructions: formData.instructions || '',
      };

      dispatch(addToCart(cartItem));
      showSuccess('Producto agregado al carrito');
      setTimeout(() => navigate('/cart'), 1000);
    } catch (error) {
      showError(error.message || 'Error al agregar al carrito');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <Loader />;
  if (!product) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#ffc107', marginBottom: '16px' }}></i>
        <h3>Producto no encontrado</h3>
        <Link to="/personalizados/ia" className="btn btn-dark mt-3">Ver todos los productos</Link>
      </div>
    );
  }

  const typeInfo = PRODUCT_TYPES[product.productType] || PRODUCT_TYPES.otro;

  return (
    <>
      <SEO
        title={`${product.name} - Personalizado`}
        description={product.description}
        url={`/personalizado/${product._id}`}
      />

      <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
        {/* Breadcrumb */}
        <div style={{ background: '#fff', borderBottom: '1px solid #eee', padding: '12px 0' }}>
          <div className="container">
            <nav style={{ fontSize: '0.85rem', color: '#888' }}>
              <Link to="/" style={{ color: '#888', textDecoration: 'none' }}>Inicio</Link>
              <span style={{ margin: '0 8px' }}>/</span>
              <Link to="/personalizados/ia" style={{ color: '#888', textDecoration: 'none' }}>Personalizados IA</Link>
              <span style={{ margin: '0 8px' }}>/</span>
              <span style={{ color: '#333', fontWeight: '600' }}>{product.name}</span>
            </nav>
          </div>
        </div>

        <div className="container" style={{ padding: '30px 20px' }}>
          <div className="row">
            {/* Columna izquierda: Imágenes del producto */}
            <div className="col-lg-6 mb-4">
              <div style={{
                background: '#fff', borderRadius: '16px', overflow: 'hidden',
                border: '1px solid #eee', boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
              }}>
                {/* Imagen principal */}
                <div style={{ position: 'relative', paddingTop: '100%', background: '#f0f0f0' }}>
                  {product.images && product.images.length > 0 ? (
                    <img
                      src={getDisplayImageUrl(product.images[selectedImage] || product.images[0])}
                      alt={product.name}
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{
                      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb'
                    }}>
                      <i className={`fas ${typeInfo.icon}`} style={{ fontSize: '4rem' }}></i>
                    </div>
                  )}
                  <span style={{
                    position: 'absolute', top: '12px', left: '12px',
                    background: '#1f8e38', color: '#fff', padding: '6px 14px',
                    borderRadius: '20px', fontSize: '0.85rem', fontWeight: '600'
                  }}>
                    <i className={`fas ${typeInfo.icon}`} style={{ marginRight: '6px' }}></i>
                    {typeInfo.label}
                  </span>
                </div>

                {/* Thumbnails */}
                {product.images && product.images.length > 1 && (
                  <div style={{ display: 'flex', gap: '8px', padding: '12px', overflowX: 'auto' }}>
                    {product.images.map((img, i) => (
                      <div
                        key={i}
                        onClick={() => setSelectedImage(i)}
                        style={{
                          width: '60px', height: '60px', borderRadius: '8px', overflow: 'hidden',
                          border: selectedImage === i ? '2px solid #1f8e38' : '2px solid transparent',
                          cursor: 'pointer', flexShrink: 0
                        }}
                      >
                        <img src={getDisplayImageUrl(img)} alt={`thumb-${i}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Descripción */}
              <div style={{
                background: '#fff', borderRadius: '16px', padding: '20px',
                border: '1px solid #eee', marginTop: '16px'
              }}>
                <h5 style={{ fontWeight: '700', marginBottom: '10px' }}>Descripción</h5>
                <p style={{ color: '#555', lineHeight: '1.6', margin: 0 }}>{product.description}</p>
                {product.tags && product.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '12px' }}>
                    {product.tags.map((tag, i) => (
                      <span key={i} style={{
                        background: '#f0f0f0', padding: '4px 10px', borderRadius: '20px',
                        fontSize: '0.8rem', color: '#666'
                      }}>#{tag}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Recordatorio: Crear tu propio producto IA */}
              <div style={{
                background: 'linear-gradient(135deg, #e8f5e9 0%, #f0faf2 100%)',
                borderRadius: '16px', padding: '20px',
                border: '2px solid #1f8e38', marginTop: '16px',
                position: 'relative', overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute', top: '-20px', right: '-20px',
                  width: '80px', height: '80px', background: 'rgba(31,142,56,0.08)',
                  borderRadius: '50%'
                }} />
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                  <div style={{
                    background: '#1f8e38', borderRadius: '12px', padding: '10px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    <i className="fas fa-wand-magic-sparkles" style={{ color: '#fff', fontSize: '1.3rem' }}></i>
                  </div>
                  <div>
                    <h6 style={{ fontWeight: '800', color: '#1f8e38', marginBottom: '6px', fontSize: '0.95rem' }}>
                      ¡Creá tu propio producto con IA!
                    </h6>
                    <p style={{ color: '#444', fontSize: '0.85rem', lineHeight: '1.5', margin: 0 }}>
                      También podés generar tus propios diseños únicos con inteligencia artificial y comprarlos directamente.
                    </p>
                    <a
                      href="https://ai.rementerprises.uy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        marginTop: '10px', background: '#1f8e38', color: '#fff',
                        padding: '8px 18px', borderRadius: '25px', fontSize: '0.85rem',
                        fontWeight: '700', textDecoration: 'none', transition: 'all 0.3s',
                        boxShadow: '0 2px 8px rgba(31,142,56,0.3)'
                      }}
                    >
                      <i className="fas fa-robot"></i>
                      Crear con IA
                      <i className="fas fa-arrow-right" style={{ fontSize: '0.75rem' }}></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Columna derecha: Formulario de pedido */}
            <div className="col-lg-6">
              <div style={{
                background: '#fff', borderRadius: '16px', padding: '28px',
                border: '1px solid #eee', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                position: 'sticky', top: '100px'
              }}>
                <h3 style={{ fontWeight: '800', marginBottom: '4px' }}>{product.name}</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: '800', color: '#1f8e38', marginBottom: '20px' }}>
                  ${product.basePrice?.toLocaleString()}
                  <span style={{ fontSize: '0.9rem', fontWeight: '400', color: '#999', marginLeft: '6px' }}>c/u</span>
                </div>

                <form onSubmit={handleSubmit}>
                  {/* Subir foto */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ fontWeight: '700', marginBottom: '8px', display: 'block' }}>
                      <i className="fas fa-camera" style={{ marginRight: '6px', color: '#1f8e38' }}></i>
                      Tu Foto para Personalizar *
                    </label>
                    {imagePreview ? (
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <img src={imagePreview} alt="Preview"
                          style={{ maxWidth: '100%', maxHeight: '250px', borderRadius: '12px', border: '2px solid #1f8e38' }} />
                        <button type="button" onClick={() => { setCustomImage(null); setImagePreview(null); }}
                          style={{
                            position: 'absolute', top: '-8px', right: '-8px',
                            background: '#dc3545', color: '#fff', border: 'none',
                            borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ) : (
                      <label style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        border: '2px dashed #1f8e38', borderRadius: '12px', padding: '30px', cursor: 'pointer',
                        background: '#f0faf2', transition: 'all 0.3s', textAlign: 'center'
                      }}>
                        <input type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={handleImageChange} hidden />
                        <i className="fas fa-cloud-upload-alt" style={{ fontSize: '2.5rem', color: '#1f8e38', marginBottom: '10px' }}></i>
                        <span style={{ fontWeight: '600', color: '#333' }}>Hacé clic para subir tu foto</span>
                        <span style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>
                          JPG, PNG, WebP o GIF (máx 10MB)
                        </span>
                      </label>
                    )}
                  </div>

                  {/* Tamaño */}
                  {product.sizes && product.sizes.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ fontWeight: '700', marginBottom: '8px', display: 'block' }}>
                        <i className="fas fa-ruler" style={{ marginRight: '6px', color: '#1f8e38' }}></i>
                        Tamaño
                      </label>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {product.sizes.map(size => (
                          <button type="button" key={size}
                            className={`btn ${formData.selectedSize === size ? 'btn-dark' : 'btn-outline-dark'}`}
                            onClick={() => setFormData(p => ({ ...p, selectedSize: size }))}>
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Cantidad */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ fontWeight: '700', marginBottom: '8px', display: 'block' }}>Cantidad</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <button type="button" className="btn btn-outline-dark"
                        onClick={() => setFormData(p => ({ ...p, quantity: Math.max(1, p.quantity - 1) }))}>
                        <i className="fas fa-minus"></i>
                      </button>
                      <span style={{ fontSize: '1.2rem', fontWeight: '700', minWidth: '40px', textAlign: 'center' }}>
                        {formData.quantity}
                      </span>
                      <button type="button" className="btn btn-outline-dark"
                        onClick={() => setFormData(p => ({ ...p, quantity: p.quantity + 1 }))}>
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>
                  </div>

                  {/* Campos personalizados del producto */}
                  {product.customFields && product.customFields.length > 0 && (
                    <div style={{ borderTop: '1px solid #eee', paddingTop: '16px', marginBottom: '16px' }}>
                      <h6 style={{ fontWeight: '700', marginBottom: '12px' }}>
                        <i className="fas fa-pen-fancy" style={{ marginRight: '6px', color: '#1f8e38' }}></i>
                        Datos para tu producto
                      </h6>
                      {product.customFields.map((field, idx) => (
                        <div key={idx} className="mb-2">
                          {field.fieldType === 'select' ? (
                            <select className="form-control"
                              value={customFieldValues[field.label] || ''}
                              onChange={e => setCustomFieldValues(p => ({ ...p, [field.label]: e.target.value }))}>
                              <option value="">{field.placeholder || `Seleccioná ${field.label}`}</option>
                              {field.options?.map((opt, oi) => (
                                <option key={oi} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={field.fieldType === 'number' ? 'number' : field.fieldType === 'date' ? 'date' : 'text'}
                              className="form-control"
                              placeholder={`${field.placeholder || field.label}${field.required ? ' *' : ''}`}
                              value={customFieldValues[field.label] || ''}
                              onChange={e => setCustomFieldValues(p => ({ ...p, [field.label]: e.target.value }))}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Instrucciones */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ fontWeight: '700', marginBottom: '8px', display: 'block' }}>
                      <i className="fas fa-comment" style={{ marginRight: '6px', color: '#1f8e38' }}></i>
                      Instrucciones adicionales (opcional)
                    </label>
                    <textarea className="form-control" name="instructions" rows="3"
                      placeholder="Ej: Quiero la foto centrada, con fondo blanco..."
                      value={formData.instructions} onChange={handleInputChange} />
                  </div>

                  {/* Total */}
                  <div style={{
                    background: '#f0faf2', borderRadius: '12px', padding: '16px', marginBottom: '20px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}>
                    <span style={{ fontWeight: '600', fontSize: '1.1rem' }}>Total</span>
                    <span style={{ fontWeight: '800', fontSize: '1.5rem', color: '#1f8e38' }}>
                      ${totalPrice.toLocaleString()}
                    </span>
                  </div>

                  {/* Botón agregar al carrito */}
                  <button type="submit" disabled={isSubmitting}
                    style={{
                      width: '100%', padding: '14px', border: 'none', borderRadius: '12px',
                      background: isSubmitting ? '#999' : '#1f8e38', color: '#fff',
                      fontWeight: '700', fontSize: '1.1rem', cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      transition: 'background 0.3s'
                    }}>
                    {isSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Subiendo foto...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-cart-plus"></i>
                        Agregar al carrito
                      </>
                    )}
                  </button>

                  <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#999', marginTop: '10px' }}>
                    <i className="fas fa-shield-halved" style={{ marginRight: '4px' }}></i>
                    Completá la compra en el checkout
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomizableProductPage;
